import { Star, Award, Trophy, Crown, Sparkles, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface RatingBadgeProps {
  rating: number;
  className?: string;
  showLabel?: boolean;
}

const RatingBadge = ({ rating, className = "", showLabel = true }: RatingBadgeProps) => {
  // Only show badge for ratings 4.5 and above
  if (rating < 4.5) return null;

  const getBadgeConfig = (rating: number) => {
    if (rating >= 5.0) {
      return {
        label: "Excelencia Perfecta",
        icon: Crown,
        gradient: "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500",
        textColor: "text-amber-950",
        borderColor: "border-amber-400",
        glow: "shadow-[0_0_20px_rgba(251,191,36,0.6)]",
      };
    } else if (rating >= 4.9) {
      return {
        label: "Elite Premium",
        icon: Trophy,
        gradient: "bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600",
        textColor: "text-white",
        borderColor: "border-purple-400",
        glow: "shadow-[0_0_15px_rgba(168,85,247,0.5)]",
      };
    } else if (rating >= 4.8) {
      return {
        label: "Excelente Plus",
        icon: Sparkles,
        gradient: "bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600",
        textColor: "text-white",
        borderColor: "border-cyan-400",
        glow: "shadow-[0_0_12px_rgba(6,182,212,0.4)]",
      };
    } else if (rating >= 4.7) {
      return {
        label: "Muy Destacado",
        icon: Zap,
        gradient: "bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600",
        textColor: "text-white",
        borderColor: "border-emerald-400",
        glow: "shadow-[0_0_10px_rgba(16,185,129,0.4)]",
      };
    } else if (rating >= 4.6) {
      return {
        label: "Certificado",
        icon: Award,
        gradient: "bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500",
        textColor: "text-orange-950",
        borderColor: "border-orange-400",
        glow: "shadow-[0_0_8px_rgba(251,146,60,0.3)]",
      };
    } else {
      return {
        label: "Calidad Superior",
        icon: Star,
        gradient: "bg-gradient-to-r from-rose-500 via-pink-400 to-rose-500",
        textColor: "text-rose-950",
        borderColor: "border-rose-400",
        glow: "shadow-[0_0_6px_rgba(244,63,94,0.3)]",
      };
    }
  };

  const config = getBadgeConfig(rating);
  const Icon = config.icon;

  return (
    <Badge
      className={`
        ${config.gradient} 
        ${config.textColor} 
        ${config.borderColor}
        ${config.glow}
        border-2 
        font-bold 
        px-3 
        py-1.5 
        flex 
        items-center 
        gap-1.5
        animate-pulse-subtle
        hover:scale-105
        transition-transform
        ${className}
      `}
    >
      <Icon className="w-4 h-4" />
      {showLabel && <span className="text-xs">{config.label}</span>}
      <span className="text-xs font-extrabold ml-1">{rating.toFixed(1)}⭐</span>
    </Badge>
  );
};

export default RatingBadge;
